/**
 * Normaliza lo que devuelve `useRouteError()` antes de mandarlo a Sentry.
 *
 * El data router de React Router ATRAPA lo que lanza un loader o una action y
 * pinta el `errorElement`. Al atraparlo deja de ser una excepción no
 * gestionada, así que no llega ni a `window.onerror` ni al `ErrorBoundary` de
 * Sentry —ese solo ve errores de RENDER—. Hay que capturarlo a mano.
 *
 * Y hay que normalizarlo: `useRouteError()` devuelve un `Response` cuando el
 * fallo es HTTP, y pasarle eso a `captureException` produce un Issue inútil
 * titulado «Non-Error exception captured».
 */
export type RouteErrorKind = 'response' | 'error' | 'unknown';

export interface RouteErrorCapture {
  error: Error;
  kind: RouteErrorKind;
  status?: number;
}

/** Forma mínima de un error de ruta HTTP. */
interface RouteErrorResponseLike {
  status?: number;
  statusText?: string;
}

/**
 * `isResponse` lo calcula quien llama con `isRouteErrorResponse()` de
 * react-router. Se recibe como parámetro para que este módulo no dependa del
 * router y el test no tenga que montarlo.
 *
 * Devuelve `null` cuando el error NO debe capturarse.
 */
export function buildRouteErrorCapture(
  routeError: unknown,
  isResponse: boolean,
): RouteErrorCapture | null {
  if (isResponse) {
    const response = (routeError ?? {}) as RouteErrorResponseLike;
    // Una dirección que no existe no es una avería: la pintan igual los bots y
    // los enlaces viejos, y llenaría Sentry de ruido sin nada que arreglar.
    if (response.status === 404) return null;
    const statusText = response.statusText || 'sin statusText';
    return {
      error: new Error(`Route error ${response.status}: ${statusText}`),
      kind: 'response',
      status: response.status,
    };
  }

  // Un Error de verdad se pasa TAL CUAL: envolverlo destruiría el stack trace
  // y con él la resolución por source maps.
  if (routeError instanceof Error) {
    return { error: routeError, kind: 'error' };
  }

  return {
    error: new Error(`Non-Error route error: ${String(routeError)}`),
    kind: 'unknown',
  };
}
